/**
 * scanReviewDisplay — honest progress copy for the still-image upload review.
 *
 * The Scan Pokédex review row used to render the raw count as
 * `{progressSeen ?? "?"} / {progressTotal ?? "?"}`, which printed a bare
 * "? / ?" whenever the digit crop could not read the header `X / Y` total.
 * "? / ?" looks like a broken progress counter — the user has no idea whether
 * the scan read nothing, read cells but missed the total, or simply needs a Dex
 * type confirmed. This helper replaces that placeholder with copy that states
 * exactly what is (and isn't) known, so a missing total never masquerades as a
 * valid count.
 *
 * Pure module: unit-testable with tsx + node:assert like the rest of
 * client/src/lib.
 */

export interface ScanProgressInput {
  /** The header "caught" count, or null when the digit crop missed it. */
  progressSeen: number | null;
  /** The header "total" count, or null when the digit crop missed it. */
  progressTotal: number | null;
  /** How many grid cells the sweep actually read in this frame. */
  cellsRead: number;
  /** True when the Dex variant could not be resolved and awaits confirmation. */
  needsConfirmation: boolean;
}

/**
 * Build the prominent review-row label. Never returns "? / ?".
 *
 * Priority:
 *   1. A real `seen / total` count when BOTH were read — the only case where a
 *      slash count is honest.
 *   2. "Dex type needs confirmation" when the variant is still unresolved — the
 *      most actionable thing the user must do before any count matters.
 *   3. "N cells read" when at least one cell was read but no header total — the
 *      scan saw entries, it just couldn't read the page's X / Y total.
 *   4. "Dex total unavailable" when nothing usable was read.
 */
export function formatScanProgress(input: ScanProgressInput): string {
  const { progressSeen, progressTotal, cellsRead, needsConfirmation } = input;

  if (typeof progressSeen === "number" && typeof progressTotal === "number") {
    return `${progressSeen} / ${progressTotal} caught`;
  }
  if (needsConfirmation) {
    return "Dex type needs confirmation";
  }
  if (cellsRead > 0) {
    return `${cellsRead} cell${cellsRead === 1 ? "" : "s"} read`;
  }
  return "Dex total unavailable";
}
